import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const profileUnsubRef = useRef(null);

  useEffect(() => {
    const authUnsub = onAuthStateChanged(auth, (firebaseUser) => {
      // Tear down any existing profile listener before switching users.
      if (profileUnsubRef.current) {
        profileUnsubRef.current();
        profileUnsubRef.current = null;
      }

      if (firebaseUser) {
        setUser(firebaseUser);
        // Real-time listener — Stripe webhook writes to this doc;
        // the UI reflects subscription changes instantly.
        profileUnsubRef.current = onSnapshot(
          doc(db, 'users', firebaseUser.uid),
          (snap) => {
            if (snap.exists()) setUserProfile(snap.data());
            setLoading(false);
          }
        );
      } else {
        setUser(null);
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => {
      authUnsub();
      if (profileUnsubRef.current) profileUnsubRef.current();
    };
  }, []);

  const signup = async (email, password, displayName) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName });
    return cred;
  };

  const createUserProfile = async (uid, data) => {
    await setDoc(doc(db, 'users', uid), {
      ...data,
      createdAt: new Date().toISOString(),
    });
    setUserProfile(data);
  };

  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);
  const logout = () => signOut(auth);

  const OWNER_EMAILS = ['billylw75@gmail.com', 'joannedwilliams86@gmail.com'];
  const isPaid = userProfile?.subscription === 'active' || OWNER_EMAILS.includes(user?.email?.toLowerCase());

  return (
    <AuthContext.Provider value={{ user, userProfile, isPaid, loading, signup, login, logout, createUserProfile }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
